// File: js/db/schema.js
//
// The ONE Dexie schema for this application, with ONE version chain (spec §7).
//
// This module exists to kill audit C5. Today `db.version(1)` is declared in FOUR
// modules — `app.js:20`, `cashierATransactions.js:12`, `cashierBTransactions.js:12`
// and `sync.js:12` — on the SAME database name, and `sync.js` uses a DIFFERENT
// primary key for `transactions` (`'++local_id, id, …'` vs `'id, …'`). Dexie does
// not error on this: `verifyInstalledSchema` ignores the `recreate` flag that a
// primary-key change sets. On a fresh browser `SyncService.init()` happens to open
// first, so offline saves work; after the first logout the store is recreated with
// keyPath `id` and no auto-increment, and every `db.transactions.add(...)` throws
// `DataError` from then on until the user clears browser storage.
//
// One module. One name. One chain. Later slices ADD a `db.version(n)` here and
// nowhere else — additive only, because an installed device must UPGRADE, never
// reset (a reset is a reset of cash).
//
// ── Why a NEW database name ──────────────────────────────────────────────────
// The legacy database is `HospitalRevenueDB`. Opening that name with this store
// set would make Dexie DROP the legacy `transactions` store on upgrade — and on a
// real device that store may still hold `sync_status:'pending'` rows, i.e. money
// already collected and not yet sent. Destroying it is precisely the C5 failure
// we are here to fix. The money engine therefore lives in its own database,
// `ombessa`, and the legacy one is left byte-untouched for the step-4 migration
// to read at its leisure.

import Dexie from 'dexie';

/** The money engine's database name. Never reuse the legacy `HospitalRevenueDB`. */
export const DB_NAME = 'ombessa';

/**
 * Build an `ombessa` Dexie handle.
 *
 * The application uses the single shared `db` exported below. Tests build isolated
 * handles by passing a unique `name` plus a fresh `indexedDB` factory, so no test
 * can see another test's cash.
 *
 * ### Version 1 stores
 *
 * Money engine (spec §7) — the four stores the sync engine owns:
 *
 * - `ledger` — this device's mirror of the append-only event log: its own writes
 *   plus everything the pull brings back. Primary key `id` is the client-minted
 *   `crypto.randomUUID()` (invariant 2). `synced` is **0/1, never a boolean** —
 *   IndexedDB cannot index booleans, so a boolean index silently matches nothing
 *   and the drain would find no work. `[device_id+business_date]` serves the
 *   day-close fold.
 * - `outbox` — `{ id (the SAME UUID as the ledger row), queued_at, payload }`.
 *   **There is no status column**: state is table membership. Pending = a row
 *   exists here. Synced = `ledger.synced === 1` and this row is gone. Conflict =
 *   a `conflicts` row exists and the ledger mirror row is KEPT, so collected cash
 *   stays visible on screen.
 * - `conflicts` — local mirror of parked events, keyed by the EVENT uuid so the
 *   push-ack path and the pull path converge on one row instead of two. Indexed on
 *   `status` because the banner counts `open` only (a count that included
 *   `reinjected`/`rejected` would stay lit forever after resolution and die of
 *   alarm fatigue).
 * - `meta` — key/value: `device_id`, `block` `{start,end,next}`, `persist_granted`,
 *   `last_pull_at`.
 *
 * Reference-data caches — `users`, `revenueTypes`, `drugs`. These are pure caches
 * of server rows (refilled with `bulkPut`, which the audit already credits as
 * correctly idempotent). They live here rather than in a second database for one
 * reason: the moment a UI module needs `new Dexie(...)` of its own, C5 grows back.
 * `drugs` deliberately carries NO `current_stock` index — stock is a fold of the
 * ledger (spec §4.1), never a stored balance.
 *
 * @param {string} [name=DB_NAME] Database name (unique per test for isolation).
 * @param {{indexedDB?: IDBFactory, IDBKeyRange?: typeof IDBKeyRange}} [options]
 * @returns {Dexie & {ledger: import('dexie').Table, outbox: import('dexie').Table,
 *   conflicts: import('dexie').Table, meta: import('dexie').Table}}
 */
export function createOmbessaDb(name = DB_NAME, options = {}) {
    const db = new Dexie(name, options);
    db.version(1).stores({
        ledger: 'id, business_date, synced, device_id, [device_id+business_date], kind, reverses_id',
        outbox: 'id, queued_at',
        conflicts: 'id, status',
        meta: 'key',

        users: 'id, email, role',
        revenueTypes: 'id, name, category',
        drugs: 'id, name'
    });
    return /** @type {any} */ (db);
}

/**
 * The single shared handle the application uses.
 *
 * Constructing a Dexie instance touches no storage — `indexedDB` is resolved at
 * `open()` — so importing this module is safe in Node and in a service worker.
 */
export const db = createOmbessaDb();

/** Every `meta` key this engine owns, so a reader never has to guess the string. */
export const META_KEYS = Object.freeze({
    DEVICE_ID: 'device_id',
    BLOCK: 'block',
    PERSIST_GRANTED: 'persist_granted',
    LAST_PULL_AT: 'last_pull_at'
});

/**
 * Read a `meta` value.
 *
 * Safe inside a Dexie transaction: the operation joins the ambient transaction
 * zone automatically, which is exactly what lets receipt-number consumption be
 * atomic with the ledger and outbox writes.
 *
 * @template T
 * @param {Dexie} database
 * @param {string} key
 * @param {T} [fallback] Returned when the key is absent.
 * @returns {Promise<T>}
 */
export async function getMeta(database, key, fallback = undefined) {
    const row = await database.table('meta').get(key);
    return row === undefined ? fallback : row.value;
}

/**
 * Write a `meta` value (upsert). Joins the ambient transaction zone when called
 * inside `db.transaction(...)`.
 *
 * @param {Dexie} database
 * @param {string} key
 * @param {*} value
 * @returns {Promise<void>}
 */
export async function setMeta(database, key, value) {
    await database.table('meta').put({ key, value });
}
